<template>
  <section class="team-goals" :class="{ 'team-goals--loading': loading }">
    <header v-if="isTrainerSelfView" class="team-goals__hero">
      <div class="hero-copy">
        <div class="hero-badges">
          <span class="trainer-self-badge"><v-icon x-small color="white">mdi-account-star-outline</v-icon> MEIN MONAT</span>
        </div>
        <h1>Mein Profil</h1>
        <p>Deine Termine, Ziele und WorkCalendar-Arbeit in einer persönlichen Monatsansicht.</p>
        <div class="hero-meta">
          <span><v-icon small>mdi-calendar-month-outline</v-icon>{{ rangeLabel }}</span>
          <span><v-icon small>mdi-office-building-marker-outline</v-icon>{{ studioLabel }}</span>
          <span v-if="lastLoadedAt"><v-icon small>mdi-database-check-outline</v-icon>Stand {{ lastLoadedAt }}</span>
        </div>
      </div>
      <div class="hero-actions">
        <v-btn class="hero-refresh" depressed :loading="loading || ownUtilizationLoading" @click="loadData(true)">
          <v-icon left>mdi-refresh</v-icon>Aktualisieren
        </v-btn>
      </div>
    </header>

    <div v-if="loading && !hasData" class="loading-shell">
      <div v-for="index in 4" :key="`skeleton-${index}`" class="skeleton-card"></div>
      <div class="skeleton-panel"></div>
    </div>

    <div v-else-if="error && !hasData" class="empty-state empty-state--error">
      <v-icon size="48" color="#fb7185">mdi-alert-circle-outline</v-icon>
      <h2>{{ isTrainerSelfView ? "Deine Daten konnten nicht geladen werden" : "Teamdaten konnten nicht geladen werden" }}</h2>
      <p>{{ error }}</p>
      <v-btn depressed color="primary" @click="loadData(true)">Erneut versuchen</v-btn>
    </div>

    <template v-else>
      <div v-if="isTrainerSelfView" class="trainer-self-view">
        <div class="kpi-grid trainer-self-kpis">
          <article v-for="card in ownKpiCards" :key="card.key" class="kpi-card" :style="{ '--kpi-color': card.color }">
            <div class="kpi-card__top">
              <span class="kpi-icon"><v-icon :color="card.color">{{ card.icon }}</v-icon></span>
            </div>
            <strong>{{ card.value }}</strong>
            <span>{{ card.label }}</span>
            <small>{{ card.hint }}</small>
          </article>
        </div>

        <trainer-activity-radar
          :profile="ownUtilization"
          :loading="ownUtilizationLoading"
          :error="ownUtilizationError"
          :range-label="rangeLabel" />

        <article v-if="ownTrainer" class="panel trainer-self-goals">
          <div class="panel-heading">
            <div>
              <span class="eyebrow">MEINE ZIELE</span>
              <h2>Dein Monatsstand</h2>
              <p>Deine persönlichen Ist-Werte und die hinterlegten Monatsziele.</p>
            </div>
            <span class="selection-chip">{{ ownTrainer.fullName }}</span>
          </div>

          <div class="detail-metrics trainer-self-goals__metrics">
            <article v-for="metric in ownTrainerMetrics" :key="`own-${metric.key}`">
              <span class="detail-metric__icon" :style="{ backgroundColor: `${metric.color}22` }">
                <v-icon :color="metric.color">{{ metric.icon }}</v-icon>
              </span>
              <div>
                <small>{{ metric.label }}</small>
                <strong>{{ formatNumber(metric.actual) }} <em v-if="metric.hasTarget">/ {{ formatNumber(metric.target) }}</em></strong>
              </div>
              <span class="detail-progress"><i :style="{ width: `${Math.min(metric.attainment, 100)}%`, backgroundColor: metric.color }"></i></span>
              <b>{{ metric.hasTarget ? `${Math.round(metric.attainment)}%` : "Kein Ziel" }}</b>
            </article>
          </div>

          <div class="trainer-self-appointments">
            <div>
              <span class="eyebrow eyebrow--blue">TERMINE</span>
              <strong>{{ appointmentActual(ownTrainer) }} Ist</strong>
              <small>{{ appointmentSourceLabel(ownTrainer) }}</small>
            </div>
            <div class="status-strip">
              <div v-for="status in trainerAppointmentStatuses(ownTrainer)" :key="`own-status-${status.key}`">
                <strong>{{ status.value }}</strong><span>{{ status.label }}</span>
              </div>
            </div>
          </div>

          <section class="trainer-self-activity">
            <div class="trainer-self-activity__heading">
              <div>
                <span class="eyebrow eyebrow--blue">MEINE AKTIVITÄTEN</span>
                <h3>Für wen hast du etwas erstellt?</h3>
                <p>Öffne eine Kategorie, um die Kunden und das jeweilige Datum zu sehen.</p>
              </div>
              <span v-if="ownActivityLoadedKey === requestKey" class="selection-chip">{{ rangeLabel }}</span>
            </div>

            <div class="trainer-self-activity__switch">
              <button
                v-for="category in ownActivityCategories"
                :key="category.key"
                type="button"
                :aria-expanded="ownActivityExpanded && activeOwnActivityKey === category.key ? 'true' : 'false'"
                :class="{ 'trainer-self-activity__category--active': ownActivityExpanded && activeOwnActivityKey === category.key }"
                @click="toggleOwnActivity(category.key)">
                <span><v-icon :color="category.color">{{ category.icon }}</v-icon></span>
                <span><strong>{{ category.label }}</strong><small>{{ formatNumber(category.count) }} im Monat</small></span>
                <v-icon color="#94a3b8">{{ ownActivityExpanded && activeOwnActivityKey === category.key ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </button>
            </div>

            <v-expand-transition>
              <div v-if="ownActivityExpanded" class="trainer-self-activity__detail">
                <div class="trainer-self-activity__detail-heading">
                  <div>
                    <span class="eyebrow">{{ activeOwnActivityCategory.label.toUpperCase() }}</span>
                    <h4 v-if="ownActivityLoadedKey === requestKey">{{ ownActivityItems.length }} Einträge im Monat</h4>
                    <h4 v-else>Kundenzuordnungen im gewählten Monat</h4>
                  </div>
                  <v-btn v-if="ownActivityLoadedKey === requestKey" icon small :loading="ownActivityLoading" @click="loadOwnActivityDetails(true)">
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </div>

                <div v-if="ownActivityLoading" class="trainer-self-activity__state">
                  <v-progress-circular indeterminate size="30" width="3" color="#38bdf8" />
                  <span>Kundenzuordnungen werden geladen …</span>
                </div>

                <v-alert v-else-if="ownActivityError" dense outlined color="error" class="ma-0">
                  {{ ownActivityError }}
                  <v-btn small text color="error" @click="loadOwnActivityDetails(true)">Erneut versuchen</v-btn>
                </v-alert>

                <div v-else-if="!ownActivityItems.length" class="trainer-self-activity__state">
                  <v-icon size="36" color="#64748b">mdi-account-search-outline</v-icon>
                  <span>Für diese Kategorie gibt es im ausgewählten Monat keine Einträge.</span>
                </div>

                <trainer-workout-history
                  v-else-if="activeOwnActivityKey === 'workouts'"
                  :rows="ownActivityItems"
                  :range-label="rangeLabel"
                  @refresh="loadOwnActivityDetails(true)" />
                <div v-else class="trainer-self-activity__list">
                  <article v-for="item in ownActivityItems" :key="`${activeOwnActivityKey}-${item.id}`">
                    <span class="trainer-self-activity__item-icon">
                      <v-icon small :color="activeOwnActivityCategory.color">{{ activeOwnActivityCategory.icon }}</v-icon>
                    </span>
                    <div>
                      <strong>{{ ownActivityCustomerName(item) }}</strong>
                      <small>{{ ownActivityItemSubtitle(item) }}</small>
                    </div>
                    <time :datetime="item.date || null">{{ formatOwnActivityDate(item.date) }}</time>
                  </article>
                </div>
              </div>
            </v-expand-transition>
          </section>
        </article>

        <v-alert v-else-if="!loading" outlined color="#64748b" class="ma-0">
          Deine Trainerkennzahlen sind für diesen Monat noch nicht verfügbar. Das WorkCalendar-Arbeitsprofil kann trotzdem bereits angezeigt werden.
        </v-alert>
      </div>

      <template v-else>
        <div class="kpi-grid kpi-grid--compact">
          <article v-for="card in kpiCards" :key="card.key" class="kpi-card kpi-card--compact" :style="{ '--kpi-color': card.color }">
            <span class="kpi-icon"><v-icon :color="card.color">{{ card.icon }}</v-icon></span>
            <span class="kpi-card__copy">
              <strong>{{ card.value }}</strong>
              <span>{{ card.label }}</span>
              <small>{{ card.hint }}</small>
            </span>
          </article>
        </div>

        <article class="panel team-goal-summary">
          <div class="panel-heading team-goal-summary__heading">
            <div>
              <span class="eyebrow">TEAM-ZIELERREICHUNG</span>
              <h2>Ist und Ziel im Team</h2>
              <p>Die gemeinsame Zielerreichung pro Kennzahl im ausgewählten Monat.</p>
            </div>
            <span class="selection-chip">{{ averageAttainment }}% im Durchschnitt</span>
          </div>
          <div class="team-goal-summary__grid">
            <article v-for="metric in aggregateMetrics" :key="`team-goal-${metric.key}`" class="team-goal-metric">
              <div class="team-goal-metric__top">
                <span class="team-goal-metric__icon" :style="{ backgroundColor: `${metric.color}1f` }">
                  <v-icon small :color="metric.color">{{ metric.icon }}</v-icon>
                </span>
                <span>
                  <strong>{{ metric.label }}</strong>
                  <small>{{ metric.group }}</small>
                </span>
                <b>{{ metric.hasTarget ? `${Math.round(metric.attainment)}%` : "–" }}</b>
              </div>
              <div class="team-goal-metric__values">
                <span>{{ formatNumber(metric.actual) }} Ist</span>
                <span>{{ metric.hasTarget ? `${formatNumber(metric.target)} Ziel` : "Kein Ziel gepflegt" }}</span>
              </div>
              <span class="team-goal-metric__progress">
                <i :style="{ width: `${Math.min(metric.attainment, 100)}%`, backgroundColor: metric.color }"></i>
              </span>
            </article>
          </div>
        </article>

        <article class="panel panel--table overview-team-panel">
          <div class="team-overview-toolbar">
            <div class="panel-heading team-overview-heading">
              <div>
                <span class="eyebrow">TEAMÜBERBLICK</span>
                <h2>Mitarbeitende im Vergleich</h2>
                <p>Ist / Ziel pro Kennzahl. Spalten anklicken, um direkt zu sortieren.</p>
              </div>
            </div>
            <div class="team-overview-controls">
              <v-text-field
                v-model.trim="search"
                dark
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                label="Mitarbeitende suchen"
                class="team-overview-search" />
              <span class="selection-chip">{{ overviewTeamRows.length }} Mitarbeitende</span>
            </div>
          </div>
          <v-data-table
            dark
            dense
            :headers="overviewTableHeaders"
            :items="overviewTeamRows"
            :items-per-page="8"
            :sort-by.sync="overviewSortBy"
            :sort-desc.sync="overviewSortDesc"
            :mobile-breakpoint="0"
            :footer-props="{ 'items-per-page-options': [8, 16, -1] }"
            no-data-text="Keine Mitarbeitenden für diese Auswahl gefunden."
            class="team-table overview-team-table">
            <template v-slot:item="{ item }">
              <tr
                class="overview-team-row"
                tabindex="0"
                @click="openTrainerDetails(item.trainer)"
                @keydown.enter.prevent="openTrainerDetails(item.trainer)">
                <td>
                  <div class="trainer-cell overview-trainer-cell">
                    <span class="person-avatar"><img :src="profileImage(item.trainer)" :alt="item.trainer.fullName" /></span>
                    <span>
                      <strong>{{ item.trainer.fullName }}</strong>
                      <small>{{ trainerStudioLabel(item.trainer) }}</small>
                    </span>
                  </div>
                </td>
                <td class="text-center">
                  <v-chip small dark color="#475569" class="overview-metric-chip">{{ formatNumber(item.percentage) }}%</v-chip>
                </td>
                <td v-for="metric in overviewMetrics" :key="`${item.trainer._id}-${metric.key}`" class="text-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        dark
                        :color="overviewMetricChipColor(item.metrics[metric.key])"
                        class="overview-metric-chip">
                        {{ formatNumber(item.metrics[metric.key].actual) }} /
                        {{ item.metrics[metric.key].hasTarget ? formatNumber(item.metrics[metric.key].target) : "–" }}
                      </v-chip>
                    </template>
                    <span>{{ overviewMetricTooltip(item.metrics[metric.key]) }}</span>
                  </v-tooltip>
                </td>
                <td class="text-right">
                  <div class="row-actions">
                    <v-btn icon small aria-label="Mitarbeiterdetails öffnen" @click.stop="openTrainerDetails(item.trainer)">
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canEditGoals"
                      icon
                      small
                      color="primary"
                      aria-label="Monatsziele bearbeiten"
                      @click.stop="openTargetDialog(item.trainer)">
                      <v-icon small>mdi-target-account</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </article>
      </template>
    </template>

    <v-dialog v-model="detailDialog" max-width="1040" content-class="team-goals-dialog">
      <v-card v-if="selectedTrainer" class="detail-card" dark>
        <div class="detail-hero">
          <span class="person-avatar person-avatar--large"><img :src="profileImage(selectedTrainer)" :alt="selectedTrainer.fullName" /></span>
          <div>
            <span class="eyebrow">MITARBEITER-DETAIL</span>
            <h2>{{ selectedTrainer.fullName }}</h2>
            <p>{{ trainerStudioLabel(selectedTrainer) }} · {{ selectedTrainer.percentage || 0 }} Stellenprozent</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            class="detail-radar-toggle"
            :class="{ 'detail-radar-toggle--active': detailRadarOpen }"
            :loading="selectedTrainerUtilizationLoading"
            @click="toggleSelectedTrainerRadar">
            <v-icon left>mdi-radar</v-icon>{{ detailRadarOpen ? "Spinnennetz schliessen" : "Spinnennetz" }}
          </v-btn>
          <v-btn v-if="canEditGoals" depressed color="primary" @click="openTargetDialog(selectedTrainer)">
            <v-icon left>mdi-target-account</v-icon>Ziele bearbeiten
          </v-btn>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="detail-body">
          <v-expand-transition>
            <div v-if="detailRadarOpen" class="detail-radar-panel">
              <trainer-activity-radar
                :key="`manager-radar-${selectedTrainer._id}-${requestKey}`"
                :profile="selectedTrainerUtilization"
                :loading="selectedTrainerUtilizationLoading"
                :error="selectedTrainerUtilizationError"
                :range-label="rangeLabel"
                :profile-name="selectedTrainer.fullName"
                variant="manager" />
            </div>
          </v-expand-transition>

          <div class="detail-metrics">
            <article v-for="metric in selectedTrainerMetrics" :key="`detail-${metric.key}`">
              <span class="detail-metric__icon" :style="{ backgroundColor: `${metric.color}22` }">
                <v-icon :color="metric.color">{{ metric.icon }}</v-icon>
              </span>
              <div>
                <small>{{ metric.label }}</small>
                <strong
                  >{{ formatNumber(metric.actual) }} <em v-if="metric.hasTarget">/ {{ formatNumber(metric.target) }}</em></strong
                >
              </div>
              <span class="detail-progress"><i :class="`tone--${metric.tone}`" :style="{ width: `${Math.min(metric.attainment, 100)}%` }"></i></span>
              <b>{{ metric.hasTarget ? `${Math.round(metric.attainment)}%` : "Kein Ziel" }}</b>
            </article>
          </div>

          <div class="my-5">
            <v-btn
              outlined
              color="#7dd3fc"
              :aria-expanded="selectedTrainerWorkoutHistoryOpen ? 'true' : 'false'"
              @click="toggleSelectedTrainerWorkoutHistory">
              <v-icon left>mdi-history</v-icon>
              {{ selectedTrainerWorkoutHistoryOpen ? "Trainingsplan-Historie schliessen" : "Trainingsplan-Historie" }}
            </v-btn>
            <v-expand-transition>
              <trainer-workout-history
                v-if="selectedTrainerWorkoutHistoryOpen"
                class="mt-4"
                :rows="selectedTrainerWorkoutHistory"
                :loading="selectedTrainerWorkoutHistoryLoading"
                :error="selectedTrainerWorkoutHistoryError"
                :range-label="rangeLabel"
                @refresh="loadSelectedTrainerWorkoutHistory(true)" />
            </v-expand-transition>
          </div>

          <div class="detail-insights">
            <article>
              <span class="eyebrow">QUALITÄT &amp; FEEDBACK</span>
              <h3>Kundenbewertung</h3>
              <div class="rating-row">
                <span>Trainerbetreuung</span>
                <v-rating
                  :value="Number(selectedTrainer.tp_question_1 || 0)"
                  color="amber"
                  background-color="blue-grey darken-2"
                  half-increments
                  dense
                  readonly />
                <strong>{{ ratingLabel(selectedTrainer.tp_question_1) }}</strong>
              </div>
              <div class="rating-row">
                <span>Trainingsplan</span>
                <v-rating
                  :value="Number(selectedTrainer.tp_question_2 || 0)"
                  color="amber"
                  background-color="blue-grey darken-2"
                  half-increments
                  dense
                  readonly />
                <strong>{{ ratingLabel(selectedTrainer.tp_question_2) }}</strong>
              </div>
            </article>

            <article>
              <span class="eyebrow eyebrow--blue">ZUSATZDETAILS</span>
              <h3>Verkauf &amp; Betreuung</h3>
              <div class="detail-facts">
                <div>
                  <strong>{{ formatNumber(selectedTrainer.avgPackages) }}</strong
                  ><span>Ø Pakete pro Vertrag</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.quote) }}%</strong><span>Abschlussquote</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.interactions && selectedTrainer.interactions.totalInteractions) }}</strong
                  ><span>Interaktionen</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.contractCountTotal) }}</strong
                  ><span>Neue Vertragskunden</span>
                </div>
              </div>
            </article>

            <article v-if="interactionLocations(selectedTrainer).length" class="detail-insights__wide">
              <span class="eyebrow eyebrow--warning">KUNDENKONTAKTE</span>
              <h3>Interaktionen nach Ort</h3>
              <div class="location-chips">
                <span v-for="location in interactionLocations(selectedTrainer)" :key="location.name">
                  {{ location.name }} <strong>{{ location.count }}</strong>
                </span>
              </div>
            </article>
          </div>

          <div class="appointment-detail">
            <div class="appointment-detail__heading">
              <div>
                <span class="eyebrow eyebrow--blue">TERMIN-IST</span>
                <h3>{{ appointmentActual(selectedTrainer) }} Termine Ist</h3>
              </div>
              <span class="source-pill" :class="`source-pill--${appointmentSource(selectedTrainer)}`">
                {{ appointmentSourceLabel(selectedTrainer) }}
              </span>
            </div>
            <div class="appointment-detail__statuses">
              <div v-for="status in trainerAppointmentStatuses(selectedTrainer)" :key="`trainer-${status.key}`">
                <v-icon :color="status.color">{{ status.icon }}</v-icon>
                <strong>{{ status.value }}</strong>
                <span>{{ status.label }}</span>
              </div>
            </div>
            <p v-if="appointmentSource(selectedTrainer) === 'manual'">
              Bis 31.07.2026 zählt ausschliesslich der fest hinterlegte Ist-Wert. WorkCalendar-Daten werden dafür nicht rückwirkend verwendet.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'manual_missing'">
              Für diesen Zeitraum vor August 2026 fehlt ein fest hinterlegter Ist-Wert. Er wird nicht durch rückwirkende WorkCalendar-Daten ersetzt.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'mixed'">
              Der Zeitraum kombiniert feste historische Ist-Werte bis 31.07.2026 mit WorkCalendar-Terminen ab 01.08.2026.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'mixed_missing'">
              Der Zeitraum kombiniert beide Datenquellen, aber mindestens einem Monat vor August 2026 fehlt der fest hinterlegte Ist-Wert.
            </p>
            <p v-else>Ab 01.08.2026 zählen alle WorkCalendar-Termine ausser stornierten, gelöschten und reinen MA-Info-Einträgen.</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="targetDialog" max-width="760" content-class="team-goals-dialog">
      <v-card v-if="targetTrainer" class="target-card" dark>
        <v-card-title>
          <div>
            <span class="eyebrow">MONATSZIELE</span>
            <h2>{{ targetTrainer.fullName }}</h2>
            <p>{{ goalMonthLabel }}</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="targetDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="target-form">
            <v-text-field
              v-for="field in targetFields"
              :key="field.key"
              v-model.number="targetDraft[field.key]"
              :label="field.label"
              :prepend-inner-icon="field.icon"
              type="number"
              min="0"
              outlined
              dense
              dark />
          </div>
          <div class="actual-readonly">
            <span><v-icon color="#60a5fa">mdi-calendar-check-outline</v-icon></span>
            <div>
              <small>Termine Ist · nicht editierbar</small>
              <strong>{{ appointmentActual(targetTrainer) }}</strong>
              <p>{{ appointmentSourceLabel(targetTrainer) }} – bis 31.07.2026 historisch fix, ab 01.08.2026 automatisch aus dem WorkCalendar.</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="targetDialog = false">Abbrechen</v-btn>
          <v-btn depressed color="primary" :loading="savingTargets" @click="saveTargets">
            <v-icon left>mdi-content-save-outline</v-icon>Ziele speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="loading && hasData" class="refresh-overlay"><v-progress-circular indeterminate color="primary" /></div>
  </section>
</template>

<script>
import api from "../../../api"
import { mapState } from "vuex"
import userService from "@/services/userService.js"
import TrainerActivityRadar from "./TrainerActivityRadar.vue"
import TrainerWorkoutHistory from "./TrainerWorkoutHistory.vue"

const TRAINER_ROLES = ["616616c4c659c875cad63525", "61661791c659c875cad63527"]

const METRICS = [
  { key: "contracts", label: "Verträge", actualKey: "contracts", targetKey: "contracts", icon: "mdi-file-sign", color: "#38bdf8", group: "Verkauf" },
  {
    key: "packages",
    label: "Pakete",
    actualKey: "packageCount",
    targetKey: "packages",
    icon: "mdi-package-variant-closed",
    color: "#818cf8",
    group: "Verkauf",
  },
  { key: "upgrades", label: "Upgrades", actualKey: "upgrades", targetKey: "upgrades", icon: "mdi-trending-up", color: "#c084fc", group: "Verkauf" },
  {
    key: "anamneses",
    label: "Anamnesen",
    actualKey: "medicalHistory",
    targetKey: "anamneses",
    icon: "mdi-clipboard-pulse-outline",
    color: "#2dd4bf",
    group: "Kundenbetreuung",
  },
  {
    key: "workouts",
    label: "Trainingspläne",
    actualKey: "workouts",
    targetKey: "workouts",
    icon: "mdi-dumbbell",
    color: "#34d399",
    group: "Kundenbetreuung",
  },
  {
    key: "workoutsPerCustomer",
    label: "Unterschiedliche TP-Kunden",
    actualKey: "workoutsPerCustomer",
    targetKey: "workoutsPerCustomer",
    icon: "mdi-account-multiple-check-outline",
    color: "#22c55e",
    group: "Kundenbetreuung",
  },
  {
    key: "interactions",
    label: "Kundeninteraktionen",
    actualKey: "interactions.totalInteractions",
    targetKey: "interactions",
    icon: "mdi-account-heart-outline",
    color: "#f59e0b",
    group: "Kundenbetreuung",
  },
  {
    key: "appointments",
    label: "Termine",
    actualKey: "appointmentsActual",
    targetKey: "appointmentsTarget",
    icon: "mdi-calendar-check-outline",
    color: "#fb7185",
    group: "Termine",
  },
]

const TARGET_FIELDS = METRICS.map((metric) => ({ key: metric.targetKey, label: `${metric.label} Ziel`, icon: metric.icon })).filter(
  (field, index, fields) => fields.findIndex((candidate) => candidate.key === field.key) === index,
)
const TEAM_GOALS_CACHE = new Map()
const TEAM_GOALS_CACHE_TTL = 2 * 60 * 1000

function hasNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
}

export default {
  name: "HomeDashboardTeamGoalsNew",
  components: { TrainerActivityRadar, TrainerWorkoutHistory },
  props: {
    studios: { type: Array, default: () => [] },
    datePickerRange: { type: Array, default: () => [] },
  },
  data() {
    return {
      trainers: [],
      loading: false,
      error: "",
      lastLoadedAt: "",
      search: "",
      overviewSortBy: "name",
      overviewSortDesc: false,
      detailDialog: false,
      selectedTrainer: null,
      detailRadarOpen: false,
      selectedTrainerUtilization: null,
      selectedTrainerUtilizationLoaded: false,
      selectedTrainerUtilizationLoading: false,
      selectedTrainerUtilizationError: "",
      selectedTrainerUtilizationRequestSerial: 0,
      selectedTrainerWorkoutHistory: [],
      selectedTrainerWorkoutHistoryOpen: false,
      selectedTrainerWorkoutHistoryLoaded: false,
      selectedTrainerWorkoutHistoryLoading: false,
      selectedTrainerWorkoutHistoryError: "",
      selectedTrainerWorkoutHistoryRequestSerial: 0,
      targetDialog: false,
      targetTrainer: null,
      targetDraft: {},
      originalTargetGoals: {},
      savingTargets: false,
      requestSerial: 0,
      loadTimer: null,
      loadedRequestKey: "",
      ownUtilization: null,
      ownUtilizationLoading: false,
      ownUtilizationError: "",
      ownUtilizationRequestSerial: 0,
      ownActivityDetails: { workouts: [], anamneses: [], contracts: [] },
      ownActivityLoading: false,
      ownActivityError: "",
      ownActivityExpanded: false,
      activeOwnActivityKey: "workouts",
      ownActivityLoadedKey: "",
      ownActivityRequestSerial: 0,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    currentUserId() {
      return String(userService.getUserId() || "")
    },
    isTrainerRole() {
      return TRAINER_ROLES.some((role) => userService.hasRole(role))
    },
    canViewTeamStatistics() {
      return userService.hasRight("stat_admin")
    },
    isTrainerSelfView() {
      return this.isTrainerRole && !this.canViewTeamStatistics
    },
    requestKey() {
      const studioIds = (this.studios || [])
        .map((studio) => String(studio?._id || studio || ""))
        .filter(Boolean)
        .sort()
      const scopeKey = this.isTrainerSelfView ? `self:${this.currentUserId}` : studioIds.join(",")
      return `${scopeKey}|${(this.datePickerRange || []).join("|")}`
    },
    hasData() {
      return this.trainers.length > 0 || Boolean(this.isTrainerSelfView && this.ownUtilization)
    },
    rangeLabel() {
      if (!this.datePickerRange?.[0]) return "Kein Monat ausgewählt"
      return this.$moment(this.datePickerRange[0]).format("MMMM YYYY")
    },
    goalMonthLabel() {
      return this.datePickerRange?.[0] ? this.$moment(this.datePickerRange[0]).format("MMMM YYYY") : "Ausgewählter Monat"
    },
    studioLabel() {
      if (this.isTrainerSelfView && this.ownUtilization?.centerLabels?.length) return this.ownUtilization.centerLabels.join(" · ")
      if (this.isTrainerSelfView) return "Meine Center"
      const names = (this.studios || []).map((studio) => studio?.name || studio?.title).filter(Boolean)
      if (!names.length) return "Kein Center ausgewählt"
      if (names.length === 1) return names[0]
      return `${names.length} Center ausgewählt`
    },
    teamTrainers() {
      return this.trainers
        .filter(
          (trainer) =>
            !String(trainer.fullName || "")
              .toLocaleLowerCase("de-CH")
              .includes("roodytrainer"),
        )
    },
    filteredTrainers() {
      const query = String(this.search || "").toLocaleLowerCase("de-CH")
      return this.teamTrainers.filter(
        (trainer) => !query || `${trainer.fullName} ${this.trainerStudioLabel(trainer)}`.toLocaleLowerCase("de-CH").includes(query),
      )
    },
    overviewMetrics() {
      return METRICS
    },
    aggregateMetrics() {
      return METRICS.map((metric) => {
        const actual = this.teamTrainers.reduce((sum, trainer) => sum + this.metricActual(trainer, metric), 0)
        const targets = this.teamTrainers
          .map((trainer) => this.metricTarget(trainer, metric))
          .filter((value) => hasNumber(value) && Number(value) > 0)
        const target = targets.reduce((sum, value) => sum + Number(value), 0)
        return { ...metric, actual, target, hasTarget: targets.length > 0, attainment: target > 0 ? (actual / target) * 100 : 0 }
      })
    },
    overviewTeamRows() {
      return this.filteredTrainers.map((trainer) => {
        const row = {
          trainer,
          name: String(trainer.fullName || ""),
          percentage: Number(trainer.percentage || 0),
          metrics: {},
        }
        METRICS.forEach((metric) => {
          const enriched = this.enrichMetric(trainer, metric)
          row.metrics[metric.key] = enriched
          row[metric.key] = enriched.actual
        })
        return row
      })
    },
    overviewTableHeaders() {
      return [
        { text: "Mitarbeitende", value: "name", width: "230px" },
        { text: "Stellenprozent", value: "percentage", align: "center" },
        ...METRICS.map((metric) => ({ text: metric.label, value: metric.key, align: "center" })),
        { text: "", value: "actions", sortable: false, align: "end", width: "88px" },
      ]
    },
    canEditGoals() {
      return userService.hasRight("stat_admin", "stat_viewstudio")
    },
    ownTrainer() {
      if (!this.isTrainerSelfView) return null
      return this.trainers.find((trainer) => String(trainer?._id || "") === this.currentUserId) || this.trainers[0] || null
    },
    ownTrainerMetrics() {
      if (!this.ownTrainer) return []
      return METRICS.map((metric) => this.enrichMetric(this.ownTrainer, metric))
    },
    ownKpiCards() {
      const trainer = this.ownTrainer
      return [
        {
          key: "appointments",
          label: "Termine Ist",
          value: trainer ? this.appointmentActual(trainer) : 0,
          hint: trainer ? this.appointmentSourceLabel(trainer) : "noch keine Daten",
          icon: "mdi-calendar-check-outline",
          color: "#38bdf8",
        },
        {
          key: "workouts",
          label: "Trainingspläne",
          value: Number(trainer?.workouts || 0),
          hint: "im gewählten Monat",
          icon: "mdi-dumbbell",
          color: "#34d399",
        },
        {
          key: "anamneses",
          label: "Anamnesen",
          value: Number(trainer?.medicalHistory || 0),
          hint: "im gewählten Monat",
          icon: "mdi-clipboard-pulse-outline",
          color: "#a78bfa",
        },
        {
          key: "interactions",
          label: "Kundenkontakte",
          value: Number(trainer?.interactions?.totalInteractions || 0),
          hint: "dokumentierte Interaktionen",
          icon: "mdi-account-heart-outline",
          color: "#f59e0b",
        },
      ]
    },
    ownActivityCategories() {
      return [
        {
          key: "workouts",
          label: "Trainingspläne",
          count: Number(this.ownTrainer?.workouts || 0),
          icon: "mdi-dumbbell",
          color: "#34d399",
        },
        {
          key: "anamneses",
          label: "Anamnesen",
          count: Number(this.ownTrainer?.medicalHistory || 0),
          icon: "mdi-clipboard-pulse-outline",
          color: "#a78bfa",
        },
        {
          key: "contracts",
          label: "Verträge",
          count: Number(this.ownTrainer?.contracts || 0),
          icon: "mdi-file-sign",
          color: "#38bdf8",
        },
      ]
    },
    activeOwnActivityCategory() {
      return this.ownActivityCategories.find((category) => category.key === this.activeOwnActivityKey) || this.ownActivityCategories[0]
    },
    ownActivityItems() {
      return Array.isArray(this.ownActivityDetails?.[this.activeOwnActivityKey]) ? this.ownActivityDetails[this.activeOwnActivityKey] : []
    },
    appointmentTotal() {
      return this.teamTrainers.reduce((sum, trainer) => sum + this.appointmentActual(trainer), 0)
    },
    averageAttainment() {
      const values = []
      this.teamTrainers.forEach((trainer) => {
        METRICS.forEach((metric) => {
          const target = this.metricTarget(trainer, metric)
          if (hasNumber(target) && Number(target) > 0) values.push(Math.min((this.metricActual(trainer, metric) / Number(target)) * 100, 200))
        })
      })
      return values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : 0
    },
    trainersWithGoals() {
      return this.teamTrainers.filter((trainer) => this.hasAnyGoal(trainer)).length
    },
    goalCoverage() {
      return this.teamTrainers.length ? Math.round((this.trainersWithGoals / this.teamTrainers.length) * 100) : 0
    },
    kpiCards() {
      return [
        {
          key: "attainment",
          label: "Ø Zielerreichung",
          value: `${this.averageAttainment}%`,
          hint: "über alle gepflegten Ziele",
          icon: "mdi-target",
          color: "#34d399",
        },
        {
          key: "goalCoverage",
          label: "Ziele gepflegt",
          value: `${this.goalCoverage}%`,
          hint: `${this.trainersWithGoals} von ${this.teamTrainers.length} Mitarbeitenden`,
          icon: "mdi-clipboard-check-outline",
          color: "#a78bfa",
        },
        {
          key: "appointments",
          label: "Termine Ist",
          value: this.appointmentTotal,
          hint: "geplant, eingecheckt oder durchgeführt",
          icon: "mdi-calendar-check-outline",
          color: "#fb7185",
        },
      ]
    },
    selectedTrainerMetrics() {
      if (!this.selectedTrainer) return []
      return METRICS.map((metric) => this.enrichMetric(this.selectedTrainer, metric))
    },
    targetFields() {
      return TARGET_FIELDS
    },
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.resetOwnActivityDetails()
        this.resetSelectedTrainerUtilization()
        this.queueLoadData()
      },
    },
    detailDialog(isOpen) {
      if (!isOpen) this.resetSelectedTrainerUtilization()
    },
  },
  beforeDestroy() {
    if (this.loadTimer) clearTimeout(this.loadTimer)
    this.requestSerial += 1
    this.ownUtilizationRequestSerial += 1
    this.ownActivityRequestSerial += 1
    this.selectedTrainerUtilizationRequestSerial += 1
    this.selectedTrainerWorkoutHistoryRequestSerial += 1
  },
  methods: {
    queueLoadData() {
      this.requestSerial += 1
      if (this.loadTimer) clearTimeout(this.loadTimer)
      this.loadTimer = setTimeout(() => {
        this.loadTimer = null
        this.loadData()
      }, 220)
    },
    async loadData(force = false) {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
      const studioIds = (this.studios || []).map((studio) => studio?._id || studio).filter(Boolean)
      if ((!this.isTrainerSelfView && !studioIds.length) || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) {
        this.trainers = []
        this.error = "Bitte mindestens ein Center und einen Monat auswählen."
        this.loading = false
        return
      }
      if (this.isTrainerSelfView) this.loadOwnUtilization()
      if (this.isTrainerSelfView && force && this.ownActivityExpanded) this.loadOwnActivityDetails(true)
      if (!force && this.loadedRequestKey === this.requestKey && this.hasData) return

      const cached = TEAM_GOALS_CACHE.get(this.requestKey)
      if (!force && cached && Date.now() - cached.createdAt < TEAM_GOALS_CACHE_TTL) {
        this.trainers = cached.trainers.map((trainer) => ({ ...trainer }))
        this.loadedRequestKey = this.requestKey
        this.lastLoadedAt = this.$moment(cached.createdAt).format("HH:mm")
        this.loading = false
        return
      }

      const requestId = ++this.requestSerial
      this.loading = true
      this.error = ""
      try {
        const result = await api.fetchTrainerDashboardInfos({
          studios: this.isTrainerSelfView ? [] : studioIds,
          range: this.datePickerRange,
          includeAppointmentFacts: true,
          compactTrainerStatistics: true,
        })
        if (requestId !== this.requestSerial) return
        const trainers = Array.isArray(result) ? result : []
        this.trainers = this.isTrainerSelfView
          ? trainers.filter((trainer) => !this.currentUserId || String(trainer?._id || "") === this.currentUserId).slice(0, 1)
          : trainers
        TEAM_GOALS_CACHE.set(this.requestKey, { createdAt: Date.now(), trainers: this.trainers.map((trainer) => ({ ...trainer })) })
        this.loadedRequestKey = this.requestKey
        this.lastLoadedAt = this.$moment().format("HH:mm")
        if (this.selectedTrainer) {
          const refreshed = this.trainers.find((trainer) => String(trainer._id) === String(this.selectedTrainer._id))
          if (refreshed) this.selectedTrainer = refreshed
        }
      } catch (error) {
        if (requestId !== this.requestSerial) return
        this.error =
          error?.response?.data?.message ||
          (this.isTrainerSelfView ? "Deine Trainerkennzahlen konnten nicht geladen werden." : "Die Teamstatistik konnte nicht geladen werden.")
        if (this.hasData && this.$toast) this.$toast.error(this.error)
        console.error("[TEAM_GOALS_NEW_LOAD]", error)
      } finally {
        if (requestId === this.requestSerial) this.loading = false
      }
    },
    async loadOwnUtilization() {
      if (!this.isTrainerSelfView || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) return
      const requestId = ++this.ownUtilizationRequestSerial
      this.ownUtilizationLoading = true
      this.ownUtilizationError = ""
      try {
        const result = await api.shiftManagement.fetchMyWorkCalendarUtilization({
          range: this.datePickerRange.map((value) => this.$moment(value).format("YYYY-MM-DD")),
        })
        if (requestId !== this.ownUtilizationRequestSerial) return
        this.ownUtilization = result?.profile || null
      } catch (error) {
        if (requestId !== this.ownUtilizationRequestSerial) return
        this.ownUtilization = null
        this.ownUtilizationError = error?.response?.data?.message || "Dein WorkCalendar-Arbeitsprofil konnte nicht geladen werden."
        console.error("[TEAM_GOALS_OWN_UTILIZATION]", error)
      } finally {
        if (requestId === this.ownUtilizationRequestSerial) this.ownUtilizationLoading = false
      }
    },
    resetOwnActivityDetails() {
      this.ownActivityRequestSerial += 1
      this.ownActivityDetails = { workouts: [], anamneses: [], contracts: [] }
      this.ownActivityLoading = false
      this.ownActivityError = ""
      this.ownActivityExpanded = false
      this.ownActivityLoadedKey = ""
    },
    toggleOwnActivity(key) {
      const shouldClose = this.ownActivityExpanded && this.activeOwnActivityKey === key
      this.activeOwnActivityKey = key
      this.ownActivityExpanded = !shouldClose
      if (this.ownActivityExpanded) this.loadOwnActivityDetails()
    },
    async loadOwnActivityDetails(force = false) {
      if (!this.isTrainerSelfView || !this.currentUserId || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) return
      if (!force && (this.ownActivityLoadedKey === this.requestKey || this.ownActivityLoading)) return
      const requestId = ++this.ownActivityRequestSerial
      this.ownActivityLoading = true
      this.ownActivityError = ""
      try {
        const result = await api.fetchTrainerDashboardInfoDetails({
          trainer: this.currentUserId,
          studios: [],
          range: this.datePickerRange.map((value) => this.$moment(value).format("YYYY-MM-DD")),
        })
        if (requestId !== this.ownActivityRequestSerial) return
        const activities = result?.activities || {}
        this.ownActivityDetails = {
          workouts: Array.isArray(activities.workouts) ? activities.workouts : [],
          anamneses: Array.isArray(activities.anamneses) ? activities.anamneses : [],
          contracts: Array.isArray(activities.contracts) ? activities.contracts : [],
        }
        this.ownActivityLoadedKey = this.requestKey
      } catch (error) {
        if (requestId !== this.ownActivityRequestSerial) return
        this.ownActivityError = error?.response?.data?.message || "Deine Kundenzuordnungen konnten nicht geladen werden."
        console.error("[TEAM_GOALS_OWN_ACTIVITY_DETAILS]", error)
      } finally {
        if (requestId === this.ownActivityRequestSerial) this.ownActivityLoading = false
      }
    },
    ownActivityCustomerName(item) {
      return item?.customer?.fullName || "Kunde nicht verfügbar"
    },
    ownActivityItemSubtitle(item) {
      if (this.activeOwnActivityKey === "workouts") return item?.title || "Trainingsplan"
      if (this.activeOwnActivityKey === "contracts") return item?.title || "Vertrag"
      return "Anamnesebogen erstellt"
    },
    formatOwnActivityDate(value) {
      const date = this.$moment(value)
      return date.isValid() ? date.format("DD.MM.YYYY") : "Datum nicht verfügbar"
    },
    nestedValue(object, path) {
      return String(path || "")
        .split(".")
        .reduce((value, key) => (value === null || value === undefined ? undefined : value[key]), object)
    },
    metricActual(trainer, metric) {
      if (metric.key === "appointments") return this.appointmentActual(trainer)
      const value = this.nestedValue(trainer, metric.actualKey)
      return hasNumber(value) ? Number(value) : 0
    },
    metricTarget(trainer, metric) {
      return trainer?.trainerGoal?.goals?.[metric.targetKey]
    },
    enrichMetric(trainer, metric) {
      const actual = this.metricActual(trainer, metric)
      const target = this.metricTarget(trainer, metric)
      const hasTarget = hasNumber(target) && Number(target) > 0
      const attainment = hasTarget ? (actual / Number(target)) * 100 : 0
      return { ...metric, actual, target: Number(target || 0), hasTarget, attainment, tone: this.attainmentTone(attainment, hasTarget) }
    },
    attainmentTone(attainment, hasTarget) {
      if (!hasTarget) return "missing"
      if (attainment >= 100) return "good"
      if (attainment >= 80) return "watch"
      return "attention"
    },
    overviewMetricChipColor(metric) {
      if (!metric?.hasTarget) return "#475569"
      if (metric.attainment >= 100) return "#15803d"
      if (metric.attainment >= 80) return "#a16207"
      return "#b91c1c"
    },
    overviewMetricTooltip(metric) {
      if (!metric) return "Keine Daten"
      if (!metric.hasTarget) return `${metric.label}: ${this.formatNumber(metric.actual)} Ist · kein Ziel hinterlegt`
      return `${metric.label}: ${this.formatNumber(metric.actual)} Ist · ${this.formatNumber(metric.target)} Ziel · ${Math.round(metric.attainment)}%`
    },
    hasAnyGoal(trainer) {
      return METRICS.some((metric) => hasNumber(this.metricTarget(trainer, metric)) && Number(this.metricTarget(trainer, metric)) > 0)
    },
    appointmentActual(trainer) {
      if (hasNumber(trainer?.appointmentsActual)) return Number(trainer.appointmentsActual)
      const legacy = trainer?.trainerGoal?.goals?.appointmentsActual
      return hasNumber(legacy) ? Number(legacy) : 0
    },
    appointmentSource(trainer) {
      if (["manual", "manual_missing", "workcalendar", "mixed", "mixed_missing"].includes(trainer?.appointmentsActualSource)) {
        return trainer.appointmentsActualSource
      }
      return hasNumber(trainer?.trainerGoal?.goals?.appointmentsActual) ? "manual" : "workcalendar"
    },
    appointmentSourceLabel(trainer) {
      return {
        manual: "Historisch fest hinterlegt",
        manual_missing: "Historischer Ist-Wert fehlt",
        workcalendar: "WorkCalendar ab 01.08.2026",
        mixed: "Historisch + WorkCalendar ab 01.08.2026",
        mixed_missing: "Gemischt · historischer Ist-Wert fehlt",
      }[this.appointmentSource(trainer)]
    },
    trainerAppointmentStatuses(trainer) {
      const breakdown = trainer?.appointmentStatusBreakdown || {}
      return [
        { key: "planned", label: "Geplant", value: Number(breakdown.planned || 0), icon: "mdi-calendar-clock-outline", color: "#60a5fa" },
        { key: "checkedin", label: "Eingecheckt", value: Number(breakdown.checkedin || 0), icon: "mdi-login", color: "#f59e0b" },
        { key: "done", label: "Durchgeführt", value: Number(breakdown.done || 0), icon: "mdi-check-circle-outline", color: "#34d399" },
        { key: "noshow", label: "No-Show", value: Number(breakdown.noshow || 0), icon: "mdi-account-off-outline", color: "#fb7185" },
      ]
    },
    ratingLabel(value) {
      return hasNumber(value) ? `${Number(value).toFixed(1)} / 5` : "Keine Daten"
    },
    interactionLocations(trainer) {
      const locations = trainer?.interactions?.locationCounts || {}
      return Object.entries(locations)
        .map(([name, count]) => ({ name: name || "Ohne Angabe", count: Number(count || 0) }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "de"))
    },
    profileImage(trainer) {
      return trainer?.picture ? `/img/profilbilder${trainer.picture}` : "/img/einmalig/imageUploadPlaceholderPFP.jpg"
    },
    studioName(id) {
      const studio = (this.storeStudios || []).find((entry) => String(entry._id) === String(id))
      return studio?.name || studio?.title || "Zusätzliches Team"
    },
    trainerStudioLabel(trainer) {
      const ids = Array.isArray(trainer?.studio) ? trainer.studio : []
      const names = [...new Set(ids.map((id) => this.studioName(id)))]
      return names.length ? names.join(" · ") : "Kein Center hinterlegt"
    },
    formatNumber(value) {
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(Number(value || 0))
    },
    resetSelectedTrainerUtilization() {
      this.selectedTrainerWorkoutHistoryRequestSerial += 1
      this.selectedTrainerWorkoutHistory = []
      this.selectedTrainerWorkoutHistoryOpen = false
      this.selectedTrainerWorkoutHistoryLoaded = false
      this.selectedTrainerWorkoutHistoryLoading = false
      this.selectedTrainerWorkoutHistoryError = ""
      this.selectedTrainerUtilizationRequestSerial += 1
      this.detailRadarOpen = false
      this.selectedTrainerUtilization = null
      this.selectedTrainerUtilizationLoaded = false
      this.selectedTrainerUtilizationLoading = false
      this.selectedTrainerUtilizationError = ""
    },
    toggleSelectedTrainerRadar() {
      this.detailRadarOpen = !this.detailRadarOpen
      if (this.detailRadarOpen && !this.selectedTrainerUtilizationLoaded) this.loadSelectedTrainerUtilization()
    },
    toggleSelectedTrainerWorkoutHistory() {
      this.selectedTrainerWorkoutHistoryOpen = !this.selectedTrainerWorkoutHistoryOpen
      if (this.selectedTrainerWorkoutHistoryOpen) this.loadSelectedTrainerWorkoutHistory()
    },
    async loadSelectedTrainerWorkoutHistory(force = false) {
      if (!this.selectedTrainer?._id || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) return
      if (!force && (this.selectedTrainerWorkoutHistoryLoaded || this.selectedTrainerWorkoutHistoryLoading)) return
      const requestId = ++this.selectedTrainerWorkoutHistoryRequestSerial
      this.selectedTrainerWorkoutHistoryLoading = true
      this.selectedTrainerWorkoutHistoryError = ""
      try {
        const result = await api.fetchTrainerDashboardInfoDetails({
          trainer: this.selectedTrainer._id,
          studios: (this.studios || []).map((studio) => studio?._id || studio).filter(Boolean),
          range: this.datePickerRange.map((value) => this.$moment(value).format("YYYY-MM-DD")),
        })
        if (requestId !== this.selectedTrainerWorkoutHistoryRequestSerial) return
        this.selectedTrainerWorkoutHistory = Array.isArray(result?.activities?.workouts) ? result.activities.workouts : []
        this.selectedTrainerWorkoutHistoryLoaded = true
      } catch (error) {
        if (requestId !== this.selectedTrainerWorkoutHistoryRequestSerial) return
        this.selectedTrainerWorkoutHistoryError = error?.response?.data?.message || "Die Trainingsplan-Historie konnte nicht geladen werden."
      } finally {
        if (requestId === this.selectedTrainerWorkoutHistoryRequestSerial) this.selectedTrainerWorkoutHistoryLoading = false
      }
    },
    async loadSelectedTrainerUtilization(force = false) {
      if (!this.selectedTrainer?._id || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) return
      if (!force && (this.selectedTrainerUtilizationLoaded || this.selectedTrainerUtilizationLoading)) return
      const studioIds = (this.studios || []).map((studio) => studio?._id || studio).filter(Boolean)
      if (!studioIds.length) {
        this.selectedTrainerUtilizationError = "Für das Arbeitsprofil muss mindestens ein Center ausgewählt sein."
        return
      }

      const requestId = ++this.selectedTrainerUtilizationRequestSerial
      this.selectedTrainerUtilizationLoading = true
      this.selectedTrainerUtilizationError = ""
      try {
        const result = await api.fetchTrainerDashboardInfoDetails({
          trainer: this.selectedTrainer._id,
          studios: studioIds,
          range: this.datePickerRange.map((value) => this.$moment(value).format("YYYY-MM-DD")),
          includeUtilization: true,
        })
        if (requestId !== this.selectedTrainerUtilizationRequestSerial) return
        this.selectedTrainerUtilization = result?.utilizationProfile || null
        this.selectedTrainerUtilizationLoaded = true
      } catch (error) {
        if (requestId !== this.selectedTrainerUtilizationRequestSerial) return
        this.selectedTrainerUtilization = null
        this.selectedTrainerUtilizationLoaded = false
        this.selectedTrainerUtilizationError = error?.response?.data?.message || "Das WorkCalendar-Arbeitsprofil konnte nicht geladen werden."
        console.error("[TEAM_GOALS_TRAINER_UTILIZATION]", error)
      } finally {
        if (requestId === this.selectedTrainerUtilizationRequestSerial) this.selectedTrainerUtilizationLoading = false
      }
    },
    openTrainerDetails(trainer) {
      this.resetSelectedTrainerUtilization()
      this.selectedTrainer = trainer
      this.detailDialog = true
    },
    openTargetDialog(trainer) {
      if (!this.canEditGoals) return
      this.targetTrainer = trainer
      this.originalTargetGoals = { ...(trainer?.trainerGoal?.goals || {}) }
      this.targetDraft = TARGET_FIELDS.reduce((draft, field) => {
        const value = this.originalTargetGoals[field.key]
        this.$set(draft, field.key, hasNumber(value) ? Number(value) : null)
        return draft
      }, {})
      this.targetDialog = true
    },
    async saveTargets() {
      if (!this.targetTrainer || !this.datePickerRange?.[0]) return
      this.savingTargets = true
      try {
        const goals = { ...this.originalTargetGoals, ...this.targetDraft }
        const saved = await api.saveTrainerGoalsForMonth({
          trainer: this.targetTrainer._id,
          month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"),
          goals,
        })
        const index = this.trainers.findIndex((trainer) => String(trainer._id) === String(this.targetTrainer._id))
        if (index >= 0) {
          const updated = { ...this.trainers[index], trainerGoal: saved || { month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"), goals } }
          this.$set(this.trainers, index, updated)
          TEAM_GOALS_CACHE.set(this.requestKey, { createdAt: Date.now(), trainers: this.trainers.map((trainer) => ({ ...trainer })) })
          this.targetTrainer = updated
          if (this.selectedTrainer && String(this.selectedTrainer._id) === String(updated._id)) this.selectedTrainer = updated
        }
        this.targetDialog = false
        if (this.$toast) this.$toast.success("Monatsziele gespeichert")
      } catch (error) {
        if (this.$toast) this.$toast.error(error?.response?.data?.message || "Monatsziele konnten nicht gespeichert werden")
      } finally {
        this.savingTargets = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.team-goals {
  --surface: #171b24;
  --surface-soft: #1d2330;
  --surface-raised: #222936;
  --border: rgba(148, 163, 184, 0.16);
  --text: #f8fafc;
  --muted: #94a3b8;
  position: relative;
  min-height: 100vh;
  padding: 22px;
  color: var(--text);
  background: radial-gradient(circle at 92% 2%, rgba(56, 189, 248, 0.12), transparent 27rem),
    radial-gradient(circle at 4% 38%, rgba(52, 211, 153, 0.08), transparent 24rem), #10131a;
}

.team-goals__hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 14px;
  padding: 28px 30px;
  border: 1px solid rgba(130, 231, 168, 0.34);
  border-radius: 24px;
  background:
    radial-gradient(circle at 88% 0%, rgba(130, 231, 168, 0.25), transparent 34%),
    linear-gradient(135deg, #009640 0%, #006f35 100%);
  box-shadow: 0 24px 60px rgba(0, 70, 34, 0.28);
}
.hero-copy h1,
.panel-heading h2,
.detail-hero h2,
.target-card h2 {
  margin: 0;
  letter-spacing: -0.035em;
}

.hero-copy h1 {
  margin-top: 8px;
  font-size: clamp(2rem, 4vw, 3.25rem);
  line-height: 1;
}
.hero-copy > p {
  max-width: 720px;
  margin: 12px 0 17px;
  color: rgba(240, 253, 244, 0.86);
  font-size: 1rem;
}
.hero-badges,
.hero-meta,
.hero-actions,
.trainer-cell,
.row-actions {
  display: flex;
  align-items: center;
}
.hero-badges {
  gap: 10px;
}
.trainer-self-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  color: #fff;
  background: rgba(0, 52, 25, 0.2);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.12em;
}
.hero-meta {
  flex-wrap: wrap;
  gap: 10px 18px;
  color: rgba(236, 253, 245, 0.78);
  font-size: 0.8rem;
}
.hero-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.hero-refresh {
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(0, 52, 25, 0.18) !important;
  text-transform: none;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.kpi-grid--compact {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.trainer-self-view {
  display: grid;
  gap: 18px;
}
.trainer-self-kpis {
  margin-bottom: 0;
}
.kpi-card {
  min-height: 156px;
  padding: 18px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: linear-gradient(160deg, rgba(34, 41, 54, 0.96), rgba(24, 29, 39, 0.96));
  box-shadow: inset 0 3px 0 var(--kpi-color);
}
.kpi-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 17px;
}
.kpi-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.09);
}
.kpi-badge,
.selection-chip {
  padding: 5px 8px;
  border-radius: 999px;
  color: #a7f3d0;
  background: rgba(16, 185, 129, 0.11);
  font-size: 0.68rem;
  font-weight: 800;
}
.kpi-card > strong {
  display: block;
  font-size: 2rem;
  line-height: 1;
}
.kpi-card > span {
  display: block;
  margin-top: 8px;
  font-weight: 800;
}
.kpi-card > small {
  display: block;
  margin-top: 4px;
  color: var(--muted);
}
.kpi-card--compact {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  align-items: center;
  gap: 13px;
  min-height: 92px;
  padding: 13px 15px;
  border-radius: 16px;
  box-shadow: inset 0 2px 0 var(--kpi-color);
}
.kpi-card--compact > span {
  margin-top: 0;
}
.kpi-card--compact .kpi-icon {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border-radius: 13px;
}
.kpi-card__copy {
  display: grid !important;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: baseline;
  column-gap: 9px;
  min-width: 0;
}
.kpi-card__copy > strong {
  font-size: 1.55rem;
  line-height: 1;
}
.kpi-card__copy > span {
  font-size: 0.78rem;
  font-weight: 800;
}
.kpi-card__copy > small {
  grid-column: 1 / -1;
  overflow: hidden;
  margin-top: 5px;
  color: var(--muted);
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-goal-summary {
  margin-bottom: 12px;
  padding: 18px;
}
.team-goal-summary__heading {
  align-items: center;
  margin-bottom: 14px;
}
.team-goal-summary__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 9px;
}
.team-goal-metric {
  min-width: 0;
  padding: 12px;
  border: 1px solid rgba(148, 163, 184, 0.11);
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.045);
}
.team-goal-metric__top {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
}
.team-goal-metric__icon {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
}
.team-goal-metric__top > span:nth-child(2),
.team-goal-metric__top strong,
.team-goal-metric__top small {
  display: block;
  min-width: 0;
}
.team-goal-metric__top strong {
  overflow: hidden;
  font-size: 0.79rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-goal-metric__top small {
  margin-top: 1px;
  color: var(--muted);
  font-size: 0.63rem;
}
.team-goal-metric__top b {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
}
.team-goal-metric__values {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 0.66rem;
}
.team-goal-metric__values span:last-child {
  overflow: hidden;
  color: var(--muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-goal-metric__progress {
  display: block;
  overflow: hidden;
  height: 4px;
  margin-top: 8px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.13);
}
.team-goal-metric__progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
}
.panel {
  overflow: hidden;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: rgba(23, 27, 36, 0.94);
  box-shadow: 0 16px 38px rgba(2, 6, 23, 0.18);
}
.trainer-self-goals {
  margin: 0;
}
.trainer-self-goals__metrics {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.trainer-self-appointments {
  display: grid;
  grid-template-columns: minmax(160px, 0.35fr) minmax(0, 1fr);
  align-items: center;
  gap: 20px;
  margin-top: 18px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: rgba(96, 165, 250, 0.055);
}
.trainer-self-appointments > div:first-child,
.trainer-self-appointments > div:first-child strong,
.trainer-self-appointments > div:first-child small {
  display: block;
}
.trainer-self-appointments > div:first-child strong {
  margin-top: 4px;
  color: #f8fafc;
  font-size: 1.15rem;
}
.trainer-self-appointments > div:first-child small {
  margin-top: 3px;
  color: var(--muted);
}
.trainer-self-appointments .status-strip {
  margin-top: 0;
}
.trainer-self-activity {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}
.trainer-self-activity__heading,
.trainer-self-activity__detail-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.trainer-self-activity__heading h3,
.trainer-self-activity__detail-heading h4 {
  margin: 4px 0 0;
  color: #f8fafc;
}
.trainer-self-activity__heading h3 {
  font-size: 1.08rem;
}
.trainer-self-activity__detail-heading h4 {
  font-size: 0.94rem;
}
.trainer-self-activity__heading p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 0.78rem;
}
.trainer-self-activity__switch {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
  margin-top: 15px;
}
.trainer-self-activity__switch button {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  color: inherit;
  background: rgba(148, 163, 184, 0.045);
  text-align: left;
  cursor: pointer;
  transition: 0.18s ease;
}
.trainer-self-activity__switch button:hover {
  border-color: rgba(96, 165, 250, 0.28);
  background: rgba(59, 130, 246, 0.07);
  transform: translateY(-1px);
}
.trainer-self-activity__switch button > span:first-child,
.trainer-self-activity__item-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.08);
}
.trainer-self-activity__switch button > span:nth-child(2),
.trainer-self-activity__switch strong,
.trainer-self-activity__switch small,
.trainer-self-activity__list strong,
.trainer-self-activity__list small {
  display: block;
  min-width: 0;
}
.trainer-self-activity__switch strong,
.trainer-self-activity__list strong {
  overflow: hidden;
  color: #f8fafc;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.trainer-self-activity__switch small,
.trainer-self-activity__list small {
  overflow: hidden;
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.trainer-self-activity__category--active {
  border-color: rgba(56, 189, 248, 0.45) !important;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.13), rgba(14, 165, 233, 0.08)) !important;
  box-shadow: inset 0 0 0 1px rgba(56, 189, 248, 0.07);
}
.trainer-self-activity__detail {
  margin-top: 12px;
  padding: 15px;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.22);
}
.trainer-self-activity__state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  min-height: 120px;
  color: var(--muted);
  font-size: 0.8rem;
  text-align: center;
}
.trainer-self-activity__list {
  display: grid;
  gap: 7px;
  max-height: 420px;
  margin-top: 13px;
  padding-right: 4px;
  overflow-y: auto;
}
.trainer-self-activity__list article {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 11px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.045);
}
.trainer-self-activity__item-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}
.trainer-self-activity__list time {
  color: #cbd5e1;
  font-size: 0.74rem;
  font-weight: 750;
  white-space: nowrap;
}
.panel--table {
  padding: 0;
}
.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}
.panel-heading h2 {
  margin-top: 3px;
  font-size: 1.25rem;
}
.panel-heading p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 0.78rem;
}
.eyebrow {
  color: #34d399;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.13em;
}
.eyebrow--warning {
  color: #fbbf24;
}
.eyebrow--blue {
  color: #60a5fa;
}
.detail-progress {
  overflow: hidden;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.13);
}
.detail-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.person-avatar {
  display: block;
  overflow: hidden;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 2px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  background: #334155;
}
.person-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.person-avatar--large {
  width: 70px;
  height: 70px;
  flex-basis: 70px;
  border-radius: 21px;
}
.status-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  margin-top: 14px;
}
.status-strip div {
  padding: 9px 5px;
  border: 1px solid var(--border);
  border-radius: 11px;
  text-align: center;
}
.status-strip strong,
.status-strip span {
  display: block;
}
.status-strip span {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.62rem;
}
.trainer-cell {
  min-width: 225px;
  gap: 10px;
  padding: 4px 0;
}
.trainer-cell > span:nth-child(2) {
  display: flex;
  flex-direction: column;
}
.trainer-cell small {
  max-width: 245px;
  overflow: hidden;
  color: var(--muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tone--good {
  background: #34d399 !important;
}
.tone--watch {
  background: #fbbf24 !important;
}
.tone--attention {
  background: #fb7185 !important;
}
.tone--missing {
  background: #64748b !important;
}
.row-actions {
  justify-content: flex-end;
  gap: 3px;
}
.team-table {
  background: transparent !important;
}
.overview-team-panel {
  min-width: 0;
}
.team-overview-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px 16px;
}
.team-overview-heading {
  margin: 0;
}
.team-overview-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}
.team-overview-search {
  width: 320px;
  max-width: 28vw;
  margin: 0 !important;
}
.team-overview-controls .selection-chip {
  flex: 0 0 auto;
  white-space: nowrap;
}
.overview-team-row {
  cursor: pointer;
}
.overview-team-row:focus {
  outline: 2px solid rgba(88, 211, 136, 0.72);
  outline-offset: -2px;
}
.overview-trainer-cell {
  min-width: 210px;
}
.overview-metric-chip {
  min-width: 74px;
  justify-content: center;
  font-size: 0.7rem !important;
  font-weight: 800;
  letter-spacing: 0 !important;
}
::v-deep .overview-team-table .v-data-table__wrapper {
  overflow-x: auto;
}
::v-deep .overview-team-table table {
  min-width: 1420px;
}
::v-deep .overview-team-table td {
  height: 62px;
  white-space: nowrap;
}
::v-deep .team-table .v-data-table__wrapper {
  border-top: 1px solid var(--border);
}
::v-deep .team-table tbody tr:hover {
  background: rgba(59, 130, 246, 0.055) !important;
}
::v-deep .team-table th {
  color: #94a3b8 !important;
  font-size: 0.67rem !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.detail-card,
.target-card {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.19) !important;
  border-radius: 22px !important;
  background: #151a23 !important;
}
.detail-hero {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 22px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(130deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95));
}
.detail-hero p,
.target-card .v-card__title p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 0.77rem;
}
.detail-body {
  padding: 22px !important;
}
.detail-radar-toggle {
  color: #bae6fd !important;
  border: 1px solid rgba(56, 189, 248, 0.25) !important;
  background: rgba(14, 165, 233, 0.09) !important;
  text-transform: none;
}
.detail-radar-toggle--active {
  color: #fff !important;
  border-color: rgba(56, 189, 248, 0.45) !important;
  background: rgba(14, 165, 233, 0.2) !important;
}
.detail-radar-panel {
  margin-bottom: 18px;
}
.detail-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.detail-metrics article {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 13px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.045);
}
.detail-metric__icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 11px;
}
.detail-metrics article > div {
  display: flex;
  flex-direction: column;
}
.detail-metrics article small {
  color: var(--muted);
}
.detail-metrics article strong {
  font-size: 1rem;
}
.detail-metrics article em {
  color: #94a3b8;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 500;
}
.detail-metrics article > b {
  color: #cbd5e1;
  font-size: 0.68rem;
}
.detail-progress {
  grid-column: 2 / -1;
  width: 100%;
}
.detail-insights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 15px;
}
.detail-insights article {
  padding: 15px;
  border: 1px solid var(--border);
  border-radius: 15px;
  background: rgba(148, 163, 184, 0.04);
}
.detail-insights h3 {
  margin: 3px 0 12px;
  font-size: 1rem;
}
.detail-insights__wide {
  grid-column: 1 / -1;
}
.rating-row {
  display: grid;
  grid-template-columns: minmax(105px, 1fr) auto 68px;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
}
.rating-row > span {
  color: #cbd5e1;
  font-size: 0.72rem;
}
.rating-row > strong {
  color: var(--muted);
  font-size: 0.66rem;
  text-align: right;
}
.detail-facts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.detail-facts div {
  padding: 9px;
  border-radius: 11px;
  background: rgba(148, 163, 184, 0.055);
}
.detail-facts strong,
.detail-facts span {
  display: block;
}
.detail-facts strong {
  font-size: 1rem;
}
.detail-facts span {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.63rem;
}
.location-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.location-chips > span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 9px;
  border-radius: 999px;
  color: #cbd5e1;
  background: rgba(245, 158, 11, 0.08);
  font-size: 0.69rem;
}
.location-chips strong {
  color: #fde68a;
}
.appointment-detail {
  margin-top: 15px;
  padding: 17px;
  border: 1px solid rgba(96, 165, 250, 0.18);
  border-radius: 16px;
  background: rgba(30, 64, 175, 0.07);
}
.appointment-detail__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.appointment-detail h3 {
  margin: 3px 0 0;
}
.source-pill {
  padding: 6px 9px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
}
.source-pill--workcalendar {
  color: #bae6fd;
  background: rgba(14, 165, 233, 0.13);
}
.source-pill--manual {
  color: #ddd6fe;
  background: rgba(139, 92, 246, 0.13);
}
.source-pill--mixed {
  color: #fde68a;
  background: rgba(245, 158, 11, 0.13);
}
.source-pill--manual_missing,
.source-pill--mixed_missing {
  color: #fecdd3;
  background: rgba(244, 63, 94, 0.14);
}
.appointment-detail__statuses {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 14px;
}
.appointment-detail__statuses div {
  display: grid;
  padding: 10px;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.055);
  text-align: center;
}
.appointment-detail__statuses strong {
  margin-top: 4px;
  font-size: 1.1rem;
}
.appointment-detail__statuses span {
  color: var(--muted);
  font-size: 0.64rem;
}
.appointment-detail > p {
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 0.72rem;
}

.target-card .v-card__title {
  padding: 22px;
  border-bottom: 1px solid var(--border);
}
.target-card .v-card__text {
  padding: 20px 22px !important;
}
.target-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 12px;
}
.actual-readonly {
  display: flex;
  gap: 13px;
  padding: 14px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 14px;
  background: rgba(37, 99, 235, 0.08);
}
.actual-readonly > span {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  place-items: center;
  border-radius: 11px;
  background: rgba(59, 130, 246, 0.12);
}
.actual-readonly div {
  display: grid;
}
.actual-readonly small {
  color: #93c5fd;
}
.actual-readonly strong {
  font-size: 1.45rem;
}
.actual-readonly p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.7rem;
}

.loading-shell {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.skeleton-card,
.skeleton-panel {
  border-radius: 20px;
  background: linear-gradient(90deg, #1b2230 25%, #252e3d 50%, #1b2230 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.skeleton-card {
  height: 156px;
}
.skeleton-panel {
  grid-column: 1 / -1;
  height: 430px;
}
@keyframes shimmer {
  to {
    background-position: -200% 0;
  }
}
.empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 18px;
  padding: 70px 20px;
  border: 1px solid var(--border);
  border-radius: 22px;
  background: var(--surface);
  text-align: center;
}
.empty-state h2 {
  margin: 12px 0 4px;
}
.empty-state p {
  max-width: 520px;
  color: var(--muted);
}
.refresh-overlay {
  position: fixed;
  z-index: 20;
  right: 25px;
  bottom: 25px;
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: #171b24;
  box-shadow: 0 14px 34px rgba(2, 6, 23, 0.45);
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .kpi-grid--compact {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .team-goal-summary__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .trainer-self-goals__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .team-goals {
    padding: 10px;
  }
  .team-goals__hero {
    align-items: flex-start;
    flex-direction: column;
    padding: 21px;
    border-radius: 20px;
  }
  .hero-actions,
  .hero-refresh {
    width: 100%;
  }
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
    gap: 9px;
  }
  .kpi-grid--compact {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .kpi-card {
    min-height: 138px;
    padding: 14px;
  }
  .kpi-card > strong {
    font-size: 1.55rem;
  }
  .kpi-card--compact {
    min-height: 82px;
    padding: 11px 13px;
  }
  .team-overview-toolbar,
  .team-overview-controls {
    align-items: stretch;
    flex-direction: column;
  }
  .team-overview-toolbar {
    gap: 12px;
    padding: 16px;
  }
  .team-overview-controls {
    gap: 8px;
  }
  .team-overview-search {
    width: 100%;
    max-width: none;
  }
  .team-goal-summary__heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .team-goal-summary__grid {
    grid-template-columns: 1fr;
  }
  .detail-metrics,
  .detail-insights,
  .target-form {
    grid-template-columns: 1fr;
  }
  .detail-insights__wide {
    grid-column: auto;
  }
  .rating-row {
    grid-template-columns: 1fr;
  }
  .rating-row > strong {
    text-align: left;
  }
  .status-strip,
  .appointment-detail__statuses {
    grid-template-columns: repeat(2, 1fr);
  }
  .trainer-self-goals__metrics,
  .trainer-self-appointments {
    grid-template-columns: 1fr;
  }
  .trainer-self-activity__heading,
  .trainer-self-activity__detail-heading {
    flex-direction: column;
  }
  .trainer-self-activity__switch {
    grid-template-columns: 1fr;
  }
  .trainer-self-activity__list article {
    grid-template-columns: 36px minmax(0, 1fr);
  }
  .trainer-self-activity__list time {
    grid-column: 2;
  }
  .detail-hero {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .detail-hero .v-spacer {
    display: none;
  }
  .detail-hero .v-btn:not(.v-btn--icon) {
    order: 4;
    width: 100%;
  }
}
</style>
